<template>
  <div class="deal-scoring-test">
    <div class="container">
      <h1>Deal Scoring Process Visualizer</h1>
      <p class="subtitle">Watch each step of the deal parsing and scoring pipeline</p>

      <!-- Input Section -->
      <div class="input-section card">
        <h2>Test Input</h2>
        <div class="form-group">
          <label>Social Media Post:</label>
          <textarea 
            v-model="testPost.postText" 
            rows="3"
            placeholder="e.g., 🔥 Nike Air Max $79.99 today only at Finish Line. Use code RUNFAST" 
            class="form-control"
          ></textarea>
        </div>
        <button @click="processPost" class="btn-primary" :disabled="isProcessing">
          {{ isProcessing ? 'Processing...' : 'Process Social Media Post' }}
        </button>
      </div>

      <!-- Pipeline Steps -->
      <div class="pipeline-steps">
        <!-- Step 0: Deal Orchestration -->
        <div class="step-card card" :class="getStepClass(-1)">
          <div class="step-header">
            <div class="step-number">0</div>
            <div class="step-info">
              <h3>Deal Orchestration</h3>
              <span class="milestone">Orchestration</span>
              <span class="endpoint-url">POST /api/scoring/process-deal</span>
            </div>
            <div class="step-status">
              <span class="status-badge" :class="results.dealOrchestration.status">
                {{ results.dealOrchestration.status }}
              </span>
            </div>
            <div class="step-actions">
              <button 
                class="btn-run-step" 
                @click="runDealOrchestration(testPost.postText)"
                :disabled="isProcessing || results.dealOrchestration.status === 'processing'"
              >
                Run Orchestration
              </button>
            </div>
          </div>
          <div class="step-content" v-if="results.dealOrchestration.data">
            <p class="step-description">Automated flow: Extract URLs → Expand URLs → Detect Retailer → Get Product Details</p>
            <div class="data-output" v-if="results.dealOrchestration.data.error">
              <div class="data-item">
                <strong>Error:</strong> <span style="color: red;">{{ results.dealOrchestration.data.error }}</span>
              </div>
            </div>
            <div class="data-output" v-else>
              <div class="data-item">
                <strong>Status:</strong> 
                <span :style="{ color: results.dealOrchestration.data.success ? 'green' : 'red' }">
                  {{ results.dealOrchestration.data.success ? 'Success' : 'Failed' }}
                </span>
              </div>
              <div class="data-item" v-if="results.dealOrchestration.data.extractedUrls && results.dealOrchestration.data.extractedUrls.length > 0">
                <strong>Extracted URLs:</strong>
                <ul class="extracted-list">
                  <li v-for="(url, idx) in results.dealOrchestration.data.extractedUrls" :key="idx">
                    <a :href="url" target="_blank">{{ url }}</a>
                  </li>
                </ul>
              </div>
              <div class="data-item" v-if="results.dealOrchestration.data.finalUrls && results.dealOrchestration.data.finalUrls.length > 0">
                <strong>Final URLs (after expansion):</strong>
                <ul class="extracted-list">
                  <li v-for="(url, idx) in results.dealOrchestration.data.finalUrls" :key="idx">
                    <a :href="url" target="_blank">{{ url }}</a>
                  </li>
                </ul>
              </div>
              <div class="data-item" v-if="results.dealOrchestration.data.retailer">
                <strong>Detected Retailer:</strong> {{ results.dealOrchestration.data.retailer }}
              </div>
              <div class="data-item" v-if="results.dealOrchestration.data.retailerUrl">
                <strong>Retailer URL:</strong> 
                <a :href="results.dealOrchestration.data.retailerUrl" target="_blank">{{ results.dealOrchestration.data.retailerUrl }}</a>
              </div>
              <div class="data-item" v-if="results.dealOrchestration.data.productDetails">
                <strong>Product Details:</strong>
                <div style="margin-left: 20px; margin-top: 8px;">
                  <div><strong>Title:</strong> {{ results.dealOrchestration.data.productDetails.title || 'N/A' }}</div>
                  <div><strong>Brand:</strong> {{ results.dealOrchestration.data.productDetails.brand || 'N/A' }}</div>
                  <div><strong>Price:</strong> {{ results.dealOrchestration.data.productDetails.price || 'N/A' }}</div>
                  <div><strong>ASIN:</strong> {{ results.dealOrchestration.data.productDetails.asin || 'N/A' }}</div>
                  <div><strong>Category:</strong> {{ results.dealOrchestration.data.productDetails.category || 'N/A' }}</div>
                  <div v-if="results.dealOrchestration.data.productDetails.productUrl">
                    <strong>Product URL:</strong> 
                    <a :href="results.dealOrchestration.data.productDetails.productUrl" target="_blank" style="color: #667eea; text-decoration: none;">{{ results.dealOrchestration.data.productDetails.productUrl }}</a>
                  </div>
                  <div v-if="results.dealOrchestration.data.productDetails.imageUrls && results.dealOrchestration.data.productDetails.imageUrls.length > 0" style="margin-top: 8px;">
                    <strong>Product Images:</strong>
                    <div style="display: flex; flex-wrap: wrap; gap: 8px; margin-top: 4px;">
                      <img 
                        v-for="(imgUrl, idx) in results.dealOrchestration.data.productDetails.imageUrls.slice(0, 3)" 
                        :key="idx"
                        :src="imgUrl" 
                        alt="Product image"
                        style="max-width: 100px; max-height: 100px; border-radius: 4px; border: 1px solid #e0e0e0;"
                      />
                    </div>
                  </div>
                  <div v-if="results.dealOrchestration.data.productDetails.features && results.dealOrchestration.data.productDetails.features.length > 0" style="margin-top: 12px;">
                    <strong>Product Features:</strong>
                    <ul class="extracted-list" style="margin-top: 4px;">
                      <li v-for="(feature, idx) in results.dealOrchestration.data.productDetails.features" :key="idx">{{ feature }}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="step-content" v-else-if="results.dealOrchestration.status === 'not-completed'">
            <p class="placeholder-text">🚧 Not completed yet</p>
          </div>
          <div class="step-content" v-else-if="results.dealOrchestration.status === 'not-started'">
            <p class="placeholder-text">⏳ Waiting for input...</p>
          </div>
        </div>

        <!-- Step 1: Claims Verification -->
        <div class="step-card card" :class="getStepClass(0)">
          <div class="step-header">
            <div class="step-number">1</div>
            <div class="step-info">
              <h3>Claims Verification</h3>
              <span class="milestone">Stage A & B</span>
              <span class="endpoint-url">POST /api/scoring/verify-claims</span>
            </div>
            <div class="step-status">
              <span class="status-badge" :class="results.claimsVerification.status">
                {{ results.claimsVerification.status }}
              </span>
            </div>
            <div class="step-actions">
              <button 
                class="btn-run-step" 
                @click="runClaimsVerification(testPost.postText)"
                :disabled="isProcessing || results.claimsVerification.status === 'processing' || !results.dealOrchestration.data?.success"
              >
                Run Step
              </button>
            </div>
          </div>
          <div class="step-content" v-if="results.claimsVerification.data">
            <p class="step-description">Stage A: Extract claims from post → Stage B: Verify with Amazon data → Reconcile</p>
            <div class="data-output" v-if="results.claimsVerification.data.error">
              <div class="data-item">
                <strong>Error:</strong> <span style="color: red;">{{ results.claimsVerification.data.error }}</span>
              </div>
            </div>
            <div class="data-output" v-else>
              <div class="data-item" v-if="results.claimsVerification.data.extractedClaims && results.claimsVerification.data.extractedClaims.length > 0">
                <strong>Extracted Claims (Stage A):</strong>
                <ul class="extracted-list">
                  <li v-for="(claim, idx) in results.claimsVerification.data.extractedClaims" :key="idx">
                    <strong>{{ claim.type }}:</strong> {{ claim.value }} <span style="color: #666; font-size: 0.9em;">(from: "{{ claim.text }}")</span>
                  </li>
                </ul>
              </div>
              <div class="data-item" v-if="results.claimsVerification.data.reconciliation">
                <strong>Reconciliation Results:</strong>
                <div style="margin-left: 20px; margin-top: 8px;">
                  <div v-if="results.claimsVerification.data.reconciliation.matches && results.claimsVerification.data.reconciliation.matches.length > 0" style="margin-bottom: 12px;">
                    <strong style="color: #4caf50;">✓ Matches ({{ results.claimsVerification.data.reconciliation.matches.length }}):</strong>
                    <ul class="extracted-list">
                      <li v-for="(match, idx) in results.claimsVerification.data.reconciliation.matches" :key="idx">
                        <strong>{{ match.claim_type }}:</strong> {{ match.claim_value }} 
                        <span style="color: #666; font-size: 0.9em;">(confidence: {{ Math.round(match.confidence * 100) }}%)</span>
                      </li>
                    </ul>
                  </div>
                  <div v-if="results.claimsVerification.data.reconciliation.mismatches && results.claimsVerification.data.reconciliation.mismatches.length > 0" style="margin-bottom: 12px;">
                    <strong style="color: #ff9800;">✗ Mismatches ({{ results.claimsVerification.data.reconciliation.mismatches.length }}):</strong>
                    <ul class="extracted-list">
                      <li v-for="(mismatch, idx) in results.claimsVerification.data.reconciliation.mismatches" :key="idx">
                        <strong>{{ mismatch.claim_type }}:</strong> Claimed "{{ mismatch.claim_value }}" 
                        <span v-if="mismatch.ground_truth_value">but actual is "{{ mismatch.ground_truth_value }}"</span>
                        <span style="color: #666; font-size: 0.9em; display: block; margin-left: 20px;">{{ mismatch.explanation }}</span>
                      </li>
                    </ul>
                  </div>
                  <div v-if="results.claimsVerification.data.reconciliation.unverified && results.claimsVerification.data.reconciliation.unverified.length > 0">
                    <strong style="color: #999;">? Unverified ({{ results.claimsVerification.data.reconciliation.unverified.length }}):</strong>
                    <ul class="extracted-list">
                      <li v-for="(unverified, idx) in results.claimsVerification.data.reconciliation.unverified" :key="idx">
                        <strong>{{ unverified.claim_type }}:</strong> {{ unverified.claim_value }}
                        <span style="color: #666; font-size: 0.9em;">({{ unverified.reason }})</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="step-content" v-else-if="results.claimsVerification.status === 'not-completed'">
            <p class="placeholder-text">🚧 Not completed yet</p>
          </div>
          <div class="step-content" v-else-if="results.claimsVerification.status === 'not-started'">
            <p class="placeholder-text">⏳ Waiting for Deal Orchestration to complete...</p>
          </div>
        </div>

        <!-- Step 2: Post Classification -->
        <div class="step-card card" :class="getStepClass(1)">
          <div class="step-header">
            <div class="step-number">2</div>
            <div class="step-info">
              <h3>Post Classification (AI)</h3>
              <span class="milestone">Milestone 1</span>
              <span class="endpoint-url">POST /api/scoring/classify-post</span>
            </div>
            <div class="step-status">
              <span class="status-badge" :class="results.postClassification.status">
                {{ results.postClassification.status }}
              </span>
            </div>
            <div class="step-actions">
              <button 
                class="btn-run-step" 
                @click="runPostClassification(testPost.postText)"
                :disabled="isProcessing || results.postClassification.status === 'processing'"
              >
                Run Step
              </button>
            </div>
          </div>
          <div class="step-content" v-if="results.postClassification.data">
            <p class="step-description">Determine if post is a deal and classify deal type using LLM</p>
            <div class="data-output">
              <div class="data-item">
                <strong>Deal Type:</strong> 
                <span class="deal-type-badge" :class="'type-' + results.postClassification.data.type.toLowerCase().replace('_', '-')">
                  {{ results.postClassification.data.type }}
                </span>
              </div>
              <div class="data-item">
                <strong>Confidence:</strong>
                <div class="confidence-bar">
                  <div class="confidence-fill" :style="{ width: (results.postClassification.data.confidence * 100) + '%' }"></div>
                  <span class="confidence-text">{{ Math.round(results.postClassification.data.confidence * 100) }}%</span>
                </div>
              </div>
              <div class="data-item">
                <strong>Product Name:</strong> {{ results.postClassification.data.product_name || 'N/A' }}
              </div>
              <div class="data-item">
                <strong>Promo Code:</strong> {{ results.postClassification.data.promo_code || 'N/A' }}
              </div>
              <div class="data-item">
                <strong>Discount Percent:</strong> {{ results.postClassification.data.discount_percent ? results.postClassification.data.discount_percent + '%' : 'N/A' }}
              </div>
            </div>
          </div>
          <div class="step-content" v-else-if="results.postClassification.status === 'not-completed'">
            <p class="placeholder-text">🚧 Not completed yet</p>
          </div>
          <div class="step-content" v-else-if="results.postClassification.status === 'not-started'">
            <p class="placeholder-text">⏳ Waiting for input...</p>
          </div>
        </div>

        <!-- Step 3: spaCy Extraction -->
        <div class="step-card card" :class="getStepClass(2)">
          <div class="step-header">
            <div class="step-number">3</div>
            <div class="step-info">
              <h3>URL & Code Extraction (spaCy)</h3>
              <span class="milestone">Milestone 2</span>
              <span class="endpoint-url">POST /api/scoring/extract-spacy</span>
            </div>
            <div class="step-status">
              <span class="status-badge" :class="results.spacyExtraction.status">
                {{ results.spacyExtraction.status }}
              </span>
            </div>
            <div class="step-actions">
              <button 
                class="btn-run-step" 
                @click="runSpacyExtraction(testPost.postText)"
                :disabled="isProcessing || results.spacyExtraction.status === 'processing'"
              >
                Run Step
              </button>
            </div>
          </div>
          <div class="step-content" v-if="results.spacyExtraction.data">
            <p class="step-description">Extract deal URL and promo code using spaCy NER model</p>
            <div class="data-output">
              <div class="data-item">
                <strong>Extracted URLs:</strong>
                <ul class="extracted-list">
                  <li v-for="(url, idx) in results.spacyExtraction.data.urls" :key="idx">
                    <a :href="url" target="_blank">{{ url }}</a>
                  </li>
                </ul>
              </div>
              <div class="data-item">
                <strong>Extracted Promo Codes:</strong>
                <ul class="extracted-list">
                  <li v-for="(code, idx) in results.spacyExtraction.data.promoCodes" :key="idx">{{ code }}</li>
                </ul>
              </div>
              <div class="data-item">
                <strong>Discount %:</strong> {{ results.spacyExtraction.data.discountPercent ? results.spacyExtraction.data.discountPercent + '%' : 'N/A' }}
              </div>
              <div class="data-item">
                <strong>Entities Found:</strong> {{ results.spacyExtraction.data.entities?.length || 0 }}
              </div>
            </div>
            <div class="raw-response-section">
              <button 
                class="toggle-raw-btn" 
                @click="showSpacyRaw = !showSpacyRaw"
              >
                {{ showSpacyRaw ? '▼' : '▶' }} Raw Response
              </button>
              <div v-if="showSpacyRaw" class="raw-response-content">
                <pre class="json-output">{{ formatJson(results.spacyExtraction.data) }}</pre>
              </div>
            </div>
          </div>
          <div class="step-content" v-else-if="results.spacyExtraction.status === 'not-completed'">
            <p class="placeholder-text">🚧 Not completed yet</p>
          </div>
          <div class="step-content" v-else-if="results.spacyExtraction.status === 'not-started'">
            <p class="placeholder-text">⏳ Waiting for previous step...</p>
          </div>
        </div>

        <!-- Step 4: GLiNER Extraction -->
        <div class="step-card card" :class="getStepClass(3)">
          <div class="step-header">
            <div class="step-number">4</div>
            <div class="step-info">
              <h3>Code Extraction (GLiNER)</h3>
              <span class="milestone">Milestone 3</span>
              <span class="endpoint-url">POST /api/scoring/extract-gliner</span>
            </div>
            <div class="step-status">
              <span class="status-badge" :class="results.glinerExtraction.status">
                {{ results.glinerExtraction.status }}
              </span>
            </div>
            <div class="step-actions">
              <button 
                class="btn-run-step" 
                @click="runGlinerExtraction(testPost.postText)"
                :disabled="isProcessing || results.glinerExtraction.status === 'processing'"
              >
                Run Step
              </button>
            </div>
          </div>
          <div class="step-content" v-if="results.glinerExtraction.data">
            <p class="step-description">Extract promo code using GLiNER model</p>
            <div class="data-output">
              <div class="data-item">
                <strong>Extracted Promo Codes:</strong>
                <ul class="extracted-list">
                  <li v-for="(code, idx) in results.glinerExtraction.data.promoCodes" :key="idx">{{ code }}</li>
                </ul>
              </div>
              <div class="data-item">
                <strong>Discount %:</strong> {{ results.glinerExtraction.data.discountPercent ? results.glinerExtraction.data.discountPercent + '%' : 'N/A' }}
              </div>
              <div class="data-item">
                <strong>Confidence Score:</strong> {{ results.glinerExtraction.data.confidence || 'N/A' }}
              </div>
            </div>
            <div class="raw-response-section">
              <button 
                class="toggle-raw-btn" 
                @click="showGlinerRaw = !showGlinerRaw"
              >
                {{ showGlinerRaw ? '▼' : '▶' }} Raw Response
              </button>
              <div v-if="showGlinerRaw" class="raw-response-content">
                <pre class="json-output">{{ formatJson(results.glinerExtraction.data) }}</pre>
              </div>
            </div>
          </div>
          <div class="step-content" v-else-if="results.glinerExtraction.status === 'not-completed'">
            <p class="placeholder-text">🚧 Not completed yet</p>
          </div>
          <div class="step-content" v-else-if="results.glinerExtraction.status === 'not-started'">
            <p class="placeholder-text">⏳ Waiting for previous step...</p>
          </div>
        </div>

        <!-- Step 5: Ingestion -->
        <div class="step-card card" :class="getStepClass(4)">
          <div class="step-header">
            <div class="step-number">5</div>
            <div class="step-info">
              <h3>URL Expansion</h3>
              <span class="milestone">Step 5</span>
              <span class="endpoint-url">POST /api/scoring/expand-url</span>
            </div>
            <div class="step-status">
              <span class="status-badge" :class="results.urlExpansion.status">
                {{ results.urlExpansion.status }}
              </span>
            </div>
            <div class="step-actions">
              <button 
                class="btn-run-step" 
                @click="runUrlExpansion()"
                :disabled="isProcessing || results.urlExpansion.status === 'processing'"
              >
                Run Step
              </button>
            </div>
          </div>
          <div class="step-content" v-if="results.urlExpansion.data">
            <p class="step-description">Expand shortened URLs to final destination</p>
            <div class="data-output">
              <div class="data-item">
                <strong>Original URL:</strong> 
                <a :href="results.urlExpansion.data.originalUrl" target="_blank">{{ results.urlExpansion.data.originalUrl }}</a>
              </div>
              <div class="data-item">
                <strong>Final URL:</strong> 
                <a :href="results.urlExpansion.data.finalUrl" target="_blank">{{ results.urlExpansion.data.finalUrl }}</a>
              </div>
            </div>
          </div>
          <div class="step-content" v-else-if="results.urlExpansion.status === 'not-completed'">
            <p class="placeholder-text">🚧 Not completed yet</p>
          </div>
          <div class="step-content" v-else-if="results.urlExpansion.status === 'not-started'">
            <p class="placeholder-text">⏳ Waiting for previous step...</p>
          </div>
        </div>

        <!-- Step 5.5: Post to Deal Processing -->
        <div class="step-card card" :class="getStepClass(5)">
          <div class="step-header">
            <div class="step-number">5.5</div>
            <div class="step-info">
              <h3>Post to Deal Processing</h3>
              <span class="milestone">Step 5.5</span>
              <span class="endpoint-url">POST /api/ProcessPostToDeal</span>
            </div>
            <div class="step-status">
              <span class="status-badge" :class="results.postToDeal.status">
                {{ results.postToDeal.status }}
              </span>
            </div>
            <div class="step-actions">
              <button 
                class="btn-run-step" 
                @click="runPostToDeal(testPost.postText)"
                :disabled="isProcessing || results.postToDeal.status === 'processing' || !testPost.postText"
              >
                Run Step
              </button>
            </div>
          </div>
          <div class="step-content" v-if="results.postToDeal.data">
            <p class="step-description">Process post through 4-step pipeline: Product Data → Discount → Promo Code → Coupon Code</p>
            <div class="data-output" v-if="results.postToDeal.data.success">
              <h4 style="margin-top: 0;">Deal Info:</h4>
              <div class="data-item">
                <strong>Product ID:</strong> {{ results.postToDeal.data.dealInfo?.productId || 'N/A' }}
              </div>
              <div class="data-item">
                <strong>Retail Price:</strong> {{ results.postToDeal.data.dealInfo?.retailPrice ? '$' + results.postToDeal.data.dealInfo.retailPrice : 'N/A' }}
              </div>
              <div class="data-item">
                <strong>Sale Price:</strong> {{ results.postToDeal.data.dealInfo?.salePrice ? '$' + results.postToDeal.data.dealInfo.salePrice : 'N/A' }}
              </div>
              <div class="data-item">
                <strong>Promo Code:</strong> {{ results.postToDeal.data.dealInfo?.promoCode || 'N/A' }}
              </div>
              <div class="data-item">
                <strong>Coupon Code:</strong> {{ results.postToDeal.data.dealInfo?.couponCode || 'N/A' }}
              </div>
              <h4 style="margin-top: 16px;">Processing Steps:</h4>
              <div class="data-item">
                <strong>Parsed URL:</strong> {{ results.postToDeal.data.steps?.parsedUrl || 'N/A' }}
              </div>
              <div class="data-item">
                <strong>Finalized URL:</strong> {{ results.postToDeal.data.steps?.finalizedUrl || 'N/A' }}
              </div>
              <div class="data-item">
                <strong>Product ID:</strong> {{ results.postToDeal.data.steps?.productId || 'N/A' }}
              </div>
              <div class="data-item" v-if="results.postToDeal.data.steps?.discountInfo">
                <strong>Discount Type:</strong> {{ results.postToDeal.data.steps.discountInfo.type }}
              </div>
              <div class="data-item" v-if="results.postToDeal.data.steps?.discountInfo">
                <strong>Discount Value:</strong> {{ results.postToDeal.data.steps.discountInfo.value }}
              </div>
              <div class="data-item" v-if="results.postToDeal.data.steps?.discountInfo">
                <strong>Final Sale Price:</strong> {{ results.postToDeal.data.steps.discountInfo.finalSalePrice ? '$' + results.postToDeal.data.steps.discountInfo.finalSalePrice : 'N/A' }}
              </div>
              <div class="data-item" v-if="results.postToDeal.data.steps?.discountInfo">
                <strong>Requires Coupon/Promo:</strong> {{ results.postToDeal.data.steps.discountInfo.requiresCouponOrPromoCode ? 'Yes' : 'No' }}
              </div>
            </div>
            <div class="data-output" v-else>
              <div class="data-item" style="color: red;">
                <strong>Error:</strong> {{ results.postToDeal.data.error || 'Unknown error' }}
              </div>
            </div>
          </div>
          <div class="step-content" v-else-if="results.postToDeal.status === 'not-completed'">
            <p class="placeholder-text">🚧 Not completed yet</p>
          </div>
          <div class="step-content" v-else-if="results.postToDeal.status === 'not-started'">
            <p class="placeholder-text">⏳ Enter post text above and click Run Step</p>
          </div>
        </div>

        <!-- Step 6: Product Details Extraction -->
        <div class="step-card card" :class="getStepClass(6)">
          <div class="step-header">
            <div class="step-number">6</div>
            <div class="step-info">
              <h3>Product Details Extraction</h3>
              <span class="milestone">Step 6</span>
              <span class="endpoint-url">POST /api/scoring/product-details</span>
            </div>
            <div class="step-status">
              <span class="status-badge" :class="results.productDetails.status">
                {{ results.productDetails.status }}
              </span>
            </div>
            <div class="step-actions">
              <button 
                class="btn-run-step" 
                @click="runProductDetails()"
                :disabled="isProcessing || results.productDetails.status === 'processing'"
              >
                Run Step
              </button>
            </div>
          </div>
          <div class="step-content" v-if="results.productDetails.data">
            <p class="step-description">Fetch product details from Amazon/Walmart/etc. API</p>
            <div class="data-output">
              <div class="data-item" v-if="results.productDetails.data.error">
                <strong>Error:</strong> <span style="color: red;">{{ results.productDetails.data.error }}</span>
              </div>
              <div class="data-item">
                <strong>Retailer:</strong> {{ results.productDetails.data.retailer || 'N/A' }}
              </div>
              <div class="data-item">
                <strong>ASIN:</strong> {{ results.productDetails.data.asin || 'N/A' }}
              </div>
              <div class="data-item">
                <strong>Product Title:</strong> {{ results.productDetails.data.title || 'N/A' }}
              </div>
              <div class="data-item">
                <strong>Brand:</strong> {{ results.productDetails.data.brand || 'N/A' }}
              </div>
              <div class="data-item">
                <strong>Price:</strong> {{ results.productDetails.data.price || 'N/A' }}
              </div>
              <div class="data-item">
                <strong>Category:</strong> {{ results.productDetails.data.category || 'N/A' }}
              </div>
              <div class="data-item" v-if="results.productDetails.data.features && results.productDetails.data.features.length > 0">
                <strong>Product Features:</strong>
                <ul class="extracted-list">
                  <li v-for="(feature, idx) in results.productDetails.data.features" :key="idx">{{ feature }}</li>
                </ul>
              </div>
              <div class="data-item" v-if="results.productDetails.data.productUrl">
                <strong>Product URL:</strong> 
                <a :href="results.productDetails.data.productUrl" target="_blank">{{ results.productDetails.data.productUrl }}</a>
              </div>
            </div>
            <div class="raw-response-section" v-if="results.productDetails.data.rawJson">
              <button 
                class="toggle-raw-btn" 
                @click="showProductDetailsRaw = !showProductDetailsRaw"
              >
                {{ showProductDetailsRaw ? '▼' : '▶' }} Raw Amazon Response
              </button>
              <div v-if="showProductDetailsRaw" class="raw-response-content">
                <pre class="json-output">{{ formatRawJson(results.productDetails.data.rawJson) }}</pre>
              </div>
            </div>
          </div>
          <div class="step-content" v-else-if="results.productDetails.status === 'not-completed'">
            <p class="placeholder-text">🚧 Not completed yet</p>
          </div>
          <div class="step-content" v-else-if="results.productDetails.status === 'not-started'">
            <p class="placeholder-text">⏳ Waiting for previous step...</p>
          </div>
        </div>

        <!-- Step 7: Ingestion -->
        <div class="step-card card" :class="getStepClass(6)">
          <div class="step-header">
            <div class="step-number">7</div>
            <div class="step-info">
              <h3>Deal Ingestion</h3>
              <span class="milestone">Milestone 8</span>
              <span class="endpoint-url">POST /api/scoring/ingest</span>
            </div>
            <div class="step-status">
              <span class="status-badge" :class="results.ingestion.status">
                {{ results.ingestion.status }}
              </span>
            </div>
            <div class="step-actions">
              <button 
                class="btn-run-step" 
                @click="runIngestion()"
                :disabled="isProcessing || results.ingestion.status === 'processing'"
              >
                Run Step
              </button>
            </div>
          </div>
          <div class="step-content" v-if="results.ingestion.data">
            <p class="step-description">Raw deal inserted into DealsRaw table</p>
            <div class="data-output">
              <div class="data-item">
                <strong>Deal ID:</strong> {{ results.ingestion.data.dealId }}
              </div>
              <div class="data-item">
                <strong>Process State:</strong> {{ results.ingestion.data.processState }}
              </div>
              <div class="data-item">
                <strong>Observed At:</strong> {{ formatDate(results.ingestion.data.observedAt) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Step 6: Normalization -->
        <div class="step-card card" :class="getStepClass(5)">
          <div class="step-header">
            <div class="step-number">6</div>
            <div class="step-info">
              <h3>Normalization</h3>
              <span class="milestone">Milestone 4</span>
              <span class="endpoint-url">POST /api/scoring/normalize</span>
            </div>
            <div class="step-status">
              <span class="status-badge" :class="results.normalization.status">
                {{ results.normalization.status }}
              </span>
            </div>
            <div class="step-actions">
              <button 
                class="btn-run-step" 
                @click="runNormalization()"
                :disabled="isProcessing || results.normalization.status === 'processing'"
              >
                Run Step
              </button>
            </div>
          </div>
          <div class="step-content" v-if="results.normalization.data">
            <p class="step-description">Extract pack count, size, units and build canonical key</p>
            <div class="data-output">
              <div class="data-item">
                <strong>Canonical Key:</strong> {{ results.normalization.data.canonicalKey }}
              </div>
              <div class="data-item">
                <strong>Pack Count:</strong> {{ results.normalization.data.packCount }}
              </div>
              <div class="data-item">
                <strong>Size/Units:</strong> {{ results.normalization.data.size }}
              </div>
              <div class="data-item">
                <strong>Normalized Title:</strong> {{ results.normalization.data.normalizedTitle }}
              </div>
              <div class="data-item">
                <strong>Confidence:</strong> 
                <div class="confidence-bar">
                  <div class="confidence-fill" :style="{ width: results.normalization.data.confidence + '%' }"></div>
                  <span class="confidence-text">{{ results.normalization.data.confidence }}%</span>
                </div>
              </div>
            </div>
          </div>
          <div class="step-content" v-else-if="results.normalization.status === 'not-started'">
            <p class="placeholder-text">⏳ Waiting for previous step...</p>
          </div>
        </div>

        <!-- Step 7: Category Placement -->
        <div class="step-card card" :class="getStepClass(6)">
          <div class="step-header">
            <div class="step-number">7</div>
            <div class="step-info">
              <h3>Category Placement</h3>
              <span class="milestone">Milestone 10</span>
              <span class="endpoint-url">POST /api/scoring/categorize (TODO)</span>
            </div>
            <div class="step-status">
              <span class="status-badge" :class="results.category.status">
                {{ results.category.status }}
              </span>
            </div>
          </div>
          <div class="step-content" v-if="results.category.data">
            <p class="step-description">Assign product to taxonomy categories</p>
            <div class="data-output">
              <div class="data-item">
                <strong>Primary Category:</strong> {{ results.category.data.primaryCategory }}
              </div>
              <div class="data-item">
                <strong>Category Path:</strong> {{ results.category.data.categoryPath }}
              </div>
              <div class="data-item">
                <strong>Secondary Categories:</strong> {{ results.category.data.secondaryCategories?.join(', ') || 'None' }}
              </div>
              <div class="data-item">
                <strong>Confidence:</strong>
                <div class="confidence-bar">
                  <div class="confidence-fill" :style="{ width: results.category.data.confidence + '%' }"></div>
                  <span class="confidence-text">{{ results.category.data.confidence }}%</span>
                </div>
              </div>
            </div>
          </div>
          <div class="step-content" v-else-if="results.category.status === 'not-completed'">
            <p class="placeholder-text">🚧 Not completed yet</p>
          </div>
          <div class="step-content" v-else-if="results.category.status === 'not-started'">
            <p class="placeholder-text">⏳ Waiting for previous step...</p>
          </div>
        </div>

        <!-- Step 8: Audience Assignment -->
        <div class="step-card card" :class="getStepClass(7)">
          <div class="step-header">
            <div class="step-number">8</div>
            <div class="step-info">
              <h3>Audience Assignment</h3>
              <span class="milestone">Milestone 11</span>
              <span class="endpoint-url">POST /api/scoring/assign-audience (TODO)</span>
            </div>
            <div class="step-status">
              <span class="status-badge" :class="results.audience.status">
                {{ results.audience.status }}
              </span>
            </div>
          </div>
          <div class="step-content" v-if="results.audience.data">
            <p class="step-description">Determine who the product is for</p>
            <div class="data-output">
              <div class="data-item">
                <strong>User Audience:</strong> {{ results.audience.data.userAudience }}
              </div>
              <div class="data-item">
                <strong>Domain Audience:</strong> {{ results.audience.data.domainAudience }}
              </div>
              <div class="data-item">
                <strong>Secondary Audiences:</strong> {{ results.audience.data.secondaryAudiences?.join(', ') || 'None' }}
              </div>
              <div class="data-item">
                <strong>Confidence:</strong>
                <div class="confidence-bar">
                  <div class="confidence-fill" :style="{ width: results.audience.data.confidence + '%' }"></div>
                  <span class="confidence-text">{{ results.audience.data.confidence }}%</span>
                </div>
              </div>
            </div>
          </div>
          <div class="step-content" v-else-if="results.audience.status === 'not-completed'">
            <p class="placeholder-text">🚧 Not completed yet</p>
          </div>
          <div class="step-content" v-else-if="results.audience.status === 'not-started'">
            <p class="placeholder-text">⏳ Waiting for previous step...</p>
          </div>
        </div>

        <!-- Step 9: Price Intelligence -->
        <div class="step-card card" :class="getStepClass(8)">
          <div class="step-header">
            <div class="step-number">9</div>
            <div class="step-info">
              <h3>Price Intelligence</h3>
              <span class="milestone">Milestone 12</span>
            </div>
            <div class="step-status">
              <span class="status-badge" :class="results.priceIntel.status">
                {{ results.priceIntel.status }}
              </span>
            </div>
          </div>
          <div class="step-content" v-if="results.priceIntel.data">
            <p class="step-description">Compare against historical price percentiles</p>
            <div class="data-output">
              <div class="data-item">
                <strong>Current Price:</strong> ${{ results.priceIntel.data.currentPrice }}
              </div>
              <div class="data-item">
                <strong>Price Position:</strong> {{ results.priceIntel.data.pricePosition }}
              </div>
              <div class="price-bands">
                <div class="band-label">Price Bands</div>
                <div class="band-row">
                  <span>P10:</span> ${{ results.priceIntel.data.p10 }}
                </div>
                <div class="band-row">
                  <span>P25:</span> ${{ results.priceIntel.data.p25 }}
                </div>
                <div class="band-row highlight">
                  <span>P50 (Median):</span> ${{ results.priceIntel.data.p50 }}
                </div>
                <div class="band-row">
                  <span>P75:</span> ${{ results.priceIntel.data.p75 }}
                </div>
                <div class="band-row">
                  <span>P90:</span> ${{ results.priceIntel.data.p90 }}
                </div>
              </div>
              <div class="data-item">
                <strong>Savings vs Median:</strong> 
                <span :class="results.priceIntel.data.savingsPercent > 0 ? 'positive' : 'neutral'">
                  {{ results.priceIntel.data.savingsPercent }}% (${{ results.priceIntel.data.savingsAmount }})
                </span>
              </div>
            </div>
          </div>
          <div class="step-content" v-else-if="results.priceIntel.status === 'not-completed'">
            <p class="placeholder-text">🚧 Not completed yet</p>
          </div>
          <div class="step-content" v-else-if="results.priceIntel.status === 'not-started'">
            <p class="placeholder-text">⏳ Waiting for previous step...</p>
          </div>
        </div>

        <!-- Step 10: Brand Intelligence -->
        <div class="step-card card" :class="getStepClass(9)">
          <div class="step-header">
            <div class="step-number">10</div>
            <div class="step-info">
              <h3>Brand Intelligence</h3>
              <span class="milestone">Milestone 13</span>
              <span class="endpoint-url">POST /api/scoring/brand-intelligence (TODO)</span>
            </div>
            <div class="step-status">
              <span class="status-badge" :class="results.brandIntel.status">
                {{ results.brandIntel.status }}
              </span>
            </div>
          </div>
          <div class="step-content" v-if="results.brandIntel.data">
            <p class="step-description">Determine brand tier and trust score</p>
            <div class="data-output">
              <div class="data-item">
                <strong>Brand Tier:</strong> 
                <span class="tier-badge" :class="'tier-' + results.brandIntel.data.tier.toLowerCase()">
                  {{ results.brandIntel.data.tier }}
                </span>
              </div>
              <div class="data-item">
                <strong>Trust Score:</strong> {{ results.brandIntel.data.trustScore }}/100
              </div>
              <div class="data-item">
                <strong>Category Relevance:</strong> {{ results.brandIntel.data.categoryRelevance }}
              </div>
            </div>
          </div>
          <div class="step-content" v-else-if="results.brandIntel.status === 'not-completed'">
            <p class="placeholder-text">🚧 Not completed yet</p>
          </div>
          <div class="step-content" v-else-if="results.brandIntel.status === 'not-started'">
            <p class="placeholder-text">⏳ Waiting for previous step...</p>
          </div>
        </div>

        <!-- Step 11: Deal Scoring -->
        <div class="step-card card" :class="getStepClass(10)">
          <div class="step-header">
            <div class="step-number">11</div>
            <div class="step-info">
              <h3>Deal Scoring</h3>
              <span class="milestone">Milestone 14</span>
              <span class="endpoint-url">POST /api/scoring/score (TODO)</span>
            </div>
            <div class="step-status">
              <span class="status-badge" :class="results.scoring.status">
                {{ results.scoring.status }}
              </span>
            </div>
          </div>
          <div class="step-content" v-if="results.scoring.data">
            <p class="step-description">Calculate final deal score and tier</p>
            <div class="data-output">
              <div class="score-display">
                <div class="final-score">
                  <span class="score-value">{{ results.scoring.data.finalScore }}</span>
                  <span class="score-label">/ 100</span>
                </div>
                <div class="score-tier" :class="'tier-' + results.scoring.data.tier.toLowerCase()">
                  {{ results.scoring.data.tier }} DEAL
                </div>
              </div>
              <div class="score-breakdown">
                <div class="breakdown-title">Score Breakdown</div>
                <div class="breakdown-item">
                  <span>Price Score:</span>
                  <span class="breakdown-value">{{ results.scoring.data.breakdown.priceScore }}</span>
                </div>
                <div class="breakdown-item">
                  <span>Brand Tier Bonus:</span>
                  <span class="breakdown-value">{{ results.scoring.data.breakdown.brandBonus }}</span>
                </div>
                <div class="breakdown-item">
                  <span>Category Relevance:</span>
                  <span class="breakdown-value">{{ results.scoring.data.breakdown.categoryScore }}</span>
                </div>
                <div class="breakdown-item">
                  <span>Mom Priors:</span>
                  <span class="breakdown-value">{{ results.scoring.data.breakdown.momPriors }}</span>
                </div>
                <div class="breakdown-item">
                  <span>Modifiers:</span>
                  <span class="breakdown-value">{{ results.scoring.data.breakdown.modifiers }}</span>
                </div>
              </div>
              <div class="explanation">
                <strong>Explanation:</strong> {{ results.scoring.data.explanation }}
              </div>
            </div>
          </div>
          <div class="step-content" v-else-if="results.scoring.status === 'not-completed'">
            <p class="placeholder-text">🚧 Not completed yet</p>
          </div>
          <div class="step-content" v-else-if="results.scoring.status === 'not-started'">
            <p class="placeholder-text">⏳ Waiting for previous step...</p>
          </div>
        </div>

        <!-- Step 12: Tagging -->
        <div class="step-card card" :class="getStepClass(11)">
          <div class="step-header">
            <div class="step-number">12</div>
            <div class="step-info">
              <h3>Tagging / Collections</h3>
              <span class="milestone">Milestone 10</span>
            </div>
            <div class="step-status">
              <span class="status-badge" :class="results.tagging.status">
                {{ results.tagging.status }}
              </span>
            </div>
          </div>
          <div class="step-content" v-if="results.tagging.data">
            <p class="step-description">Apply merchandising tags and collections</p>
            <div class="data-output">
              <div class="tags-list">
                <span v-for="tag in results.tagging.data.tags" :key="tag" class="tag-badge">
                  {{ tag }}
                </span>
              </div>
              <div class="data-item">
                <strong>Total Tags:</strong> {{ results.tagging.data.tags.length }}
              </div>
            </div>
          </div>
          <div class="step-content" v-else-if="results.tagging.status === 'not-completed'">
            <p class="placeholder-text">🚧 Not completed yet</p>
          </div>
          <div class="step-content" v-else-if="results.tagging.status === 'not-started'">
            <p class="placeholder-text">⏳ Waiting for previous step...</p>
          </div>
        </div>

        <!-- Step 13: Leaderboards -->
        <div class="step-card card" :class="getStepClass(12)">
          <div class="step-header">
            <div class="step-number">13</div>
            <div class="step-info">
              <h3>Leaderboard Placement</h3>
              <span class="milestone">Milestone 16</span>
              <span class="endpoint-url">POST /api/scoring/update-leaderboards (TODO)</span>
            </div>
            <div class="step-status">
              <span class="status-badge" :class="results.leaderboards.status">
                {{ results.leaderboards.status }}
              </span>
            </div>
          </div>
          <div class="step-content" v-if="results.leaderboards.data">
            <p class="step-description">Place deal in relevant leaderboards</p>
            <div class="data-output">
              <div class="data-item">
                <strong>Added to Leaderboards:</strong>
                <ul class="leaderboard-list">
                  <li v-for="lb in results.leaderboards.data.leaderboards" :key="lb">
                    {{ lb }}
                  </li>
                </ul>
              </div>
              <div class="data-item">
                <strong>Rank Position:</strong> #{{ results.leaderboards.data.rankPosition }}
              </div>
            </div>
          </div>
          <div class="step-content" v-else-if="results.leaderboards.status === 'not-completed'">
            <p class="placeholder-text">🚧 Not completed yet</p>
          </div>
          <div class="step-content" v-else-if="results.leaderboards.status === 'not-started'">
            <p class="placeholder-text">⏳ Waiting for previous step...</p>
          </div>
        </div>

        <!-- Step 14: Search Indexing -->
        <div class="step-card card" :class="getStepClass(13)">
          <div class="step-header">
            <div class="step-number">14</div>
            <div class="step-info">
              <h3>Search Indexing</h3>
              <span class="milestone">Milestone 17</span>
              <span class="endpoint-url">POST /api/scoring/index (TODO)</span>
            </div>
            <div class="step-status">
              <span class="status-badge" :class="results.indexing.status">
                {{ results.indexing.status }}
              </span>
            </div>
          </div>
          <div class="step-content" v-if="results.indexing.data">
            <p class="step-description">Index deal in OpenSearch for fast search</p>
            <div class="data-output">
              <div class="data-item">
                <strong>Index:</strong> {{ results.indexing.data.indexName }}
              </div>
              <div class="data-item">
                <strong>Document ID:</strong> {{ results.indexing.data.documentId }}
              </div>
              <div class="data-item">
                <strong>Status:</strong> {{ results.indexing.data.indexStatus }}
              </div>
            </div>
          </div>
          <div class="step-content" v-else-if="results.indexing.status === 'not-completed'">
            <p class="placeholder-text">🚧 Not completed yet</p>
          </div>
          <div class="step-content" v-else-if="results.indexing.status === 'not-started'">
            <p class="placeholder-text">⏳ Waiting for previous step...</p>
          </div>
        </div>
      </div>

      <!-- Summary Section -->
      <div class="summary-section card" v-if="processCompleted">
        <h2>Process Summary</h2>
        <div class="summary-stats">
          <div class="stat-item">
            <span class="stat-value">{{ completedSteps }}</span>
            <span class="stat-label">Steps Completed</span>
          </div>
          <div class="stat-item">
            <span class="stat-value">{{ totalProcessingTime }}ms</span>
            <span class="stat-label">Total Time</span>
          </div>
          <div class="stat-item">
            <span class="stat-value">{{ results.scoring.data?.finalScore || 'N/A' }}</span>
            <span class="stat-label">Final Score</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useDealScoring } from '@/composables/useDealScoring'

interface TestPost {
  postText: string
}

const testPost = ref<TestPost>({
  postText: `
  🔥30% OFF🔥
Kids Makeup Sets for Girls, Real Washable Make Up Kits Toys Toddler Children Princess Dress Up Birthday Toy for 3 4 5 6 7 8 9 10 11 12 Years (Blue)

Use promo code — 44QDMU3S

https://joylink.io/amzn/NPftPGQ
[ad]
  `
})

const {
  results,
  isProcessing,
  processPost: runProcessing,
  resetResults,
  runPostClassification,
  runSpacyExtraction,
  runGlinerExtraction,
  runUrlExpansion,
  runPostToDeal,
  runProductDetails,
  runIngestion,
  runNormalization
} = useDealScoring()

const runDealOrchestration = (_: string) => console.warn('runDealOrchestration: not implemented')
const runClaimsVerification = (_: string) => console.warn('runClaimsVerification: not implemented')

const showSpacyRaw = ref(false)
const showGlinerRaw = ref(false)
const showProductDetailsRaw = ref(false)

const formatJson = (obj: any): string => {
  return JSON.stringify(obj, null, 2)
}

const formatRawJson = (rawJson: string | any): string => {
  if (!rawJson) return ''
  try {
    // If it's already a string, parse it first
    const parsed = typeof rawJson === 'string' ? JSON.parse(rawJson) : rawJson
    return formatJson(parsed)
  } catch (e) {
    // If parsing fails, return as-is (might already be formatted)
    return typeof rawJson === 'string' ? rawJson : formatJson(rawJson)
  }
}

const processCompleted = computed(() => {
  return results.value.scoring.status === 'completed' || results.value.scoring.status === 'not-completed'
})

const completedSteps = computed(() => {
  let count = 0
  Object.values(results.value).forEach(step => {
    if (typeof step === 'object' && step !== null && 'status' in step) {
      if (step.status === 'completed') count++
    }
  })
  return count
})

const totalProcessingTime = computed(() => {
  return results.value.processingTime || 0
})

const processPost = async () => {
  await runProcessing(testPost.value.postText)
}

const getStepClass = (stepIndex: number) => {
  // Handle orchestration step (stepIndex -1)
  if (stepIndex === -1) {
    return results.value.dealOrchestration?.status || 'not-started'
  }
  
  const statuses = [
    results.value.claimsVerification?.status || 'not-started',
    results.value.postClassification?.status || 'not-started',
    results.value.spacyExtraction?.status || 'not-started',
    results.value.glinerExtraction?.status || 'not-started',
    results.value.urlExpansion?.status || 'not-started',
    results.value.postToDeal?.status || 'not-started',
    results.value.productDetails?.status || 'not-started',
    results.value.ingestion?.status || 'not-started',
    results.value.normalization?.status || 'not-started',
    results.value.category?.status || 'not-started',
    results.value.audience?.status || 'not-started',
    results.value.priceIntel?.status || 'not-started',
    results.value.brandIntel?.status || 'not-started',
    results.value.scoring?.status || 'not-started',
    results.value.tagging?.status || 'not-started',
    results.value.leaderboards?.status || 'not-started',
    results.value.indexing?.status || 'not-started'
  ]
  
  return statuses[stepIndex] || 'not-started'
}

const formatDate = (date: string) => {
  return new Date(date).toLocaleString()
}
</script>

<style scoped>
.deal-scoring-test {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 40px 20px;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
}

h1 {
  color: white;
  font-size: 2.5rem;
  margin-bottom: 10px;
  text-align: center;
}

.subtitle {
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
  font-size: 1.1rem;
  margin-bottom: 40px;
}

.card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

/* Input Section */
.input-section h2 {
  margin-top: 0;
  margin-bottom: 20px;
  color: #333;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  color: #555;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 32px;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  margin-top: 10px;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Pipeline Steps */
.pipeline-steps {
  position: relative;
}

.step-card {
  position: relative;
  transition: all 0.3s ease;
}

.step-card.completed {
  border-left: 4px solid #4caf50;
}

.step-card.processing {
  border-left: 4px solid #2196f3;
  animation: pulse 2s infinite;
}

.step-card.not-completed {
  border-left: 4px solid #ff9800;
}

.step-card.not-started {
  opacity: 0.6;
  border-left: 4px solid #ccc;
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
  50% {
    box-shadow: 0 4px 20px rgba(33, 150, 243, 0.3);
  }
}

.step-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.step-number {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
  flex-shrink: 0;
}

.step-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.step-info h3 {
  margin: 0;
  color: #333;
  font-size: 1.3rem;
}

.milestone {
  display: inline-block;
  background: #f0f0f0;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  color: #666;
  margin-right: 8px;
}

.endpoint-url {
  display: inline-block;
  background: #e3f2fd;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  color: #1976d2;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  align-self: flex-start;
}

.step-status {
  flex-shrink: 0;
}

.step-actions {
  flex-shrink: 0;
}

.btn-run-step {
  background: #667eea;
  color: white;
  border: none;
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-run-step:hover:not(:disabled) {
  background: #5568d3;
  transform: translateY(-1px);
}

.btn-run-step:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #ccc;
}

.status-badge {
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.completed {
  background: #e8f5e9;
  color: #4caf50;
}

.status-badge.processing {
  background: #e3f2fd;
  color: #2196f3;
}

.status-badge.not-completed {
  background: #fff3e0;
  color: #ff9800;
}

.status-badge.not-started {
  background: #f5f5f5;
  color: #999;
}

.step-content {
  padding-left: 64px;
}

.step-description {
  color: #666;
  font-style: italic;
  margin-bottom: 16px;
}

.data-output {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
}

.data-item {
  margin-bottom: 12px;
}

.data-item:last-child {
  margin-bottom: 0;
}

.data-item strong {
  color: #333;
  display: inline-block;
  min-width: 180px;
}

.raw-response-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
}

.toggle-raw-btn {
  background: #f0f0f0;
  border: 1px solid #d0d0d0;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.toggle-raw-btn:hover {
  background: #e0e0e0;
  border-color: #c0c0c0;
}

.raw-response-content {
  margin-top: 12px;
  background: #1e1e1e;
  border-radius: 6px;
  padding: 16px;
  overflow-x: auto;
}

.json-output {
  margin: 0;
  color: #d4d4d4;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.confidence-bar {
  display: inline-block;
  width: 200px;
  height: 24px;
  background: #e0e0e0;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  vertical-align: middle;
}

.confidence-fill {
  height: 100%;
  background: linear-gradient(90deg, #4caf50 0%, #8bc34a 100%);
  transition: width 0.3s ease;
}

.confidence-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 12px;
  font-weight: 600;
  color: #333;
}

.price-bands {
  background: white;
  padding: 12px;
  border-radius: 6px;
  margin: 12px 0;
}

.band-label {
  font-weight: 700;
  color: #333;
  margin-bottom: 8px;
}

.band-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 8px;
  border-bottom: 1px solid #f0f0f0;
}

.band-row.highlight {
  background: #fff3e0;
  font-weight: 600;
  border-radius: 4px;
}

.positive {
  color: #4caf50;
  font-weight: 600;
}

.neutral {
  color: #666;
}

.tier-badge {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 16px;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 14px;
}

.tier-badge.tier-a {
  background: #ffd700;
  color: #333;
}

.tier-badge.tier-b {
  background: #c0c0c0;
  color: #333;
}

.tier-badge.tier-c {
  background: #cd7f32;
  color: white;
}

.score-display {
  text-align: center;
  padding: 24px;
  background: white;
  border-radius: 8px;
  margin-bottom: 20px;
}

.final-score {
  font-size: 3rem;
  font-weight: 800;
  color: #333;
}

.score-value {
  color: #667eea;
}

.score-label {
  font-size: 1.5rem;
  color: #999;
}

.score-tier {
  margin-top: 12px;
  font-size: 1.5rem;
  font-weight: 700;
  padding: 8px 20px;
  border-radius: 24px;
  display: inline-block;
}

.score-tier.tier-gold {
  background: #ffd700;
  color: #333;
}

.score-tier.tier-silver {
  background: #c0c0c0;
  color: #333;
}

.score-tier.tier-bronze {
  background: #cd7f32;
  color: white;
}

.score-breakdown {
  background: white;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}

.breakdown-title {
  font-weight: 700;
  color: #333;
  margin-bottom: 12px;
  font-size: 1.1rem;
}

.breakdown-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.breakdown-value {
  font-weight: 600;
  color: #667eea;
}

.explanation {
  background: white;
  padding: 12px;
  border-radius: 6px;
  color: #555;
  line-height: 1.6;
}

.tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.tag-badge {
  background: #667eea;
  color: white;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 500;
}

.leaderboard-list {
  margin: 8px 0;
  padding-left: 20px;
}

.leaderboard-list li {
  padding: 4px 0;
  color: #555;
}

.placeholder-text {
  color: #999;
  font-size: 1.1rem;
  text-align: center;
  padding: 20px;
}

/* Summary Section */
.summary-section h2 {
  margin-top: 0;
  margin-bottom: 24px;
  color: #333;
  text-align: center;
}

.summary-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.stat-item {
  text-align: center;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.stat-value {
  display: block;
  font-size: 2.5rem;
  font-weight: 800;
  color: #667eea;
  margin-bottom: 8px;
}

.stat-label {
  display: block;
  color: #666;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.deal-type-badge {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 16px;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 12px;
}

.deal-type-badge.type-deal-product {
  background: #4caf50;
  color: white;
}

.deal-type-badge.type-deal-category {
  background: #2196f3;
  color: white;
}

.deal-type-badge.type-deal-storewide {
  background: #ff9800;
  color: white;
}

.deal-type-badge.type-non-deal {
  background: #999;
  color: white;
}

.evidence-list,
.extracted-list {
  margin: 8px 0;
  padding-left: 20px;
  list-style-type: disc;
}

.evidence-list li,
.extracted-list li {
  padding: 4px 0;
  color: #555;
}

.extracted-list a {
  color: #667eea;
  text-decoration: none;
}

.extracted-list a:hover {
  text-decoration: underline;
}

@media (max-width: 768px) {
  h1 {
    font-size: 2rem;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .summary-stats {
    grid-template-columns: 1fr;
  }

  .step-content {
    padding-left: 0;
  }
}
</style>
